import { inject, Injectable } from '@angular/core';
import {
    AppReviewEvent,
    CONFIG_TOKEN,
    ExtendedMedicalData,
    Feature,
    FeatureActivityIdentifier,
    MedicalData,
    Patient,
    PatientAddedOrRemovedEvent,
} from '@pxl/models/common';
import { CommonService, LoginService, PatientRetrieveOptions, PatientService, PxlApplicationStateService } from '@pxl/models/contracts';
import {
    DocumentToFillPatientDataModel,
    ImageModel,
    ImageUploadModel,
    PatientCreateResultModel,
    PatientDataUpdateModel,
    PatientExtendedMedicalDataModel,
    PatientinsuranceAddBody,
    PatientInsuranceModel,
    PatientInsuranceService as PatientInsuranceAPI,
    PatientinsuranceUpdateBody,
    PatientLinkageModel,
    PatientMergeRequestModel,
    PatientPrimaryAndEmergencyVeterinariansModel,
    PatientService as PatientBackendService,
    PlanToEndOnDeceasedModel,
    ReferenceDataFavoritesModel,
    SpeciesModel,
    WeightCreateModel,
    WeightModel,
} from '@pxl/shared/generated';
import { Convert, SortOrder } from '@pxl/shared/util';
import { FeatureService } from './feature.service';
import { combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { first, map } from 'rxjs/operators';

function toPatientDataUpdateModel(patient: Patient): PatientDataUpdateModel {
    return {
        name: patient.name,
        nickName: patient.nickName,
        speciesId: patient.speciesId,
        speciesText: patient.speciesText,
        breedId: patient.breedId,
        breedText: patient.breedText,
        breedTypeId: patient.breedTypeId,
        animalSpecificData:
            patient.animalSpecificData == null
                ? null
                : {
                      genderId: patient.animalSpecificData.genderId,
                      dateOfBirth: Convert.convertBirthDate2UTC(patient.animalSpecificData.dateOfBirth)?.toISOString(),
                      coatId: patient.animalSpecificData.coatId,
                      coatText: patient.animalSpecificData.coatText,
                      colorText: patient.animalSpecificData.colorText,
                      chipNo: patient.animalSpecificData.chipNo,
                      euDomesticAnimalsPass: patient.animalSpecificData.euDomesticAnimalsPass,
                      studBookNo: patient.animalSpecificData.studBookNo,
                      identification: patient.animalSpecificData.identification,
                  },
        livestockSpecificData:
            patient.livestockSpecificData == null
                ? null
                : {
                      sizeOfStock: patient.livestockSpecificData.sizeOfStock,
                      averageDateOfBirth: Convert.convertBirthDate2UTC(patient.livestockSpecificData.averageDateOfBirth)?.toISOString(),
                  },
        foodProducing: patient.foodProducing,
        deceased: patient.deceased,
        federalOperationsUnitIdentifier: patient.federalOperationsUnitIdentifier,
    };
}

function mapWeightModelIntoWeightCreateModel(weight: WeightModel): WeightCreateModel {
    return {
        timeStampUtc: weight.timeStampUtc,
        usesPoundsInsteadOfKilograms: weight.userUsesPoundsInsteadOfKilograms,
        weight: weight.value,
        estimated: weight.estimated,
    };
}

function getOptionDefaults(opts: PatientRetrieveOptions): Required<PatientRetrieveOptions> {
    if (!opts) {
        return { filterDeceasedPatients: false, filterHiddenPatients: false, includeInsurances: false };
    }

    const { filterDeceasedPatients, filterHiddenPatients, includeInsurances } = opts;
    return {
        filterDeceasedPatients: filterDeceasedPatients ?? false,
        filterHiddenPatients: filterHiddenPatients ?? false,
        includeInsurances: includeInsurances ?? false,
    };
}

@Injectable()
export class DefaultPatientService implements PatientService {
    private readonly patientsSource$$ = new Subject<Patient[]>();

    private readonly config = inject(CONFIG_TOKEN);
    private readonly loginService = inject(LoginService);
    private readonly commonService = inject(CommonService);
    private readonly patientBackendService = inject(PatientBackendService);
    private readonly applicationStateService = inject(PxlApplicationStateService);
    private readonly patientInsuranceAPI = inject(PatientInsuranceAPI);
    private readonly featureService = inject(FeatureService);

    private get apiVersion(): string {
        return this.config.API_VERSION ?? '1';
    }

    private get token(): string {
        return this.loginService.getToken();
    }

    public onPatientsChanged(): Observable<Patient[]> {
        return this.patientsSource$$.asObservable();
    }

    public async refreshPatients(opts?: PatientRetrieveOptions): Promise<Patient[]> {
        const patients = await this.updatePatientsAndNotify(undefined, {
            filterDeceasedPatients: opts?.filterDeceasedPatients ?? true,
            filterHiddenPatients: opts?.filterHiddenPatients ?? true,
            includeInsurances: opts?.includeInsurances,
        });
        return patients;
    }

    public getPatientsWithFilter(filterPatientIds?: string[], opts?: PatientRetrieveOptions): Promise<Patient[]> {
        return this.getPatientsRequest(filterPatientIds, opts);
    }

    public async getPatientWithPatientId(patientId: string | null, opts?: { includeInsurances?: boolean }): Promise<Patient | null> {
        if (!patientId) {
            return null;
        }

        return (await this.getPatientsRequest([patientId], opts)).pop() ?? null;
    }

    public async getPatientMedicalData(patientId: string): Promise<MedicalData> {
        return firstValueFrom(this.patientBackendService.getMedicalData(patientId, this.apiVersion, this.token)).then(
            (model) =>
                new MedicalData(
                    model.patientId,
                    model.bloodGroup,
                    model.intolerancesOrAllergies,
                    model.preExistingConditions,
                    model.surgeries,
                    model.bloodTransfusions,
                    model.medicationInformation,
                ),
        );
    }

    public async getPatientExtendedMedicalData(patientId: string): Promise<ExtendedMedicalData> {
        return firstValueFrom(this.patientBackendService.getExtendedMedicalData(patientId, this.apiVersion, this.token)).then(
            (model) => new ExtendedMedicalData(model.patientId, model.nonDomesticTravels, model.notes),
        );
    }

    public async getPlansToEndOnDeceased(patientId: string): Promise<PlanToEndOnDeceasedModel[]> {
        return firstValueFrom(this.patientBackendService.getPlansToEndOnDeceased(patientId, this.apiVersion, this.token));
    }

    public async merge(request: PatientMergeRequestModel): Promise<void> {
        await firstValueFrom(this.patientBackendService.merge(request, this.apiVersion, this.token));
        await this.updatePatientsAndNotify();
    }

    public getAvailableMergeTargets(patientId: string): Observable<Patient[]> {
        return this.patientBackendService
            .getAvailableMergeTargetsFor(patientId, this.apiVersion, this.token)
            .pipe(map((response) => response.availableMergeTargets.map((data) => new Patient(data)).sort(SortOrder.byName)));
    }

    public async hidePatient(patient: Patient): Promise<void> {
        await firstValueFrom(this.patientBackendService.markAsHidden(patient.patientId, this.apiVersion, this.token, true));
        await this.updatePatientsAndNotify();
    }

    public async restorePatient(patient: Patient): Promise<void> {
        await firstValueFrom(this.patientBackendService.markAsHidden(patient.patientId, this.apiVersion, this.token, false));
        await this.updatePatientsAndNotify();
    }

    public isFoodProducingVisible(speciesId: string): Observable<boolean> {
        return this.commonService.onSpecies().pipe(
            first(),
            map((speciesModels) => this.isFoodProducingVisibleWithData(speciesId, speciesModels)),
        );
    }

    public async saveAvatar(patient: Patient, viewPort: ImageUploadModel): Promise<ImageModel> {
        const imageUploadResultModel = await firstValueFrom(
            this.patientBackendService.addAvatarImage(viewPort, this.apiVersion, this.token, patient.patientId),
        );

        patient.avatarImage = imageUploadResultModel;

        // notify all other subscriber for patients
        await this.updatePatientsAndNotify();

        // notify for AppReview
        this.applicationStateService.raiseEventSpecific(
            new AppReviewEvent({ featureActivityIdentifier: FeatureActivityIdentifier.upload_patient_avatar }),
        );

        return imageUploadResultModel;
    }

    public async saveBackground(patient: Patient, viewPort: ImageUploadModel): Promise<ImageModel> {
        const imageUploadResultModel = await firstValueFrom(
            this.patientBackendService.addBackgroundImage(viewPort, this.apiVersion, this.token, patient.patientId),
        );

        patient.backgroundImage = imageUploadResultModel;

        // notify all other subscriber for patients
        await this.updatePatientsAndNotify();

        // notify for AppReview
        this.applicationStateService.raiseEventSpecific(
            new AppReviewEvent({ featureActivityIdentifier: FeatureActivityIdentifier.upload_patient_background }),
        );

        return imageUploadResultModel;
    }

    public async deleteBackground(patient: Patient): Promise<void> {
        await firstValueFrom(this.patientBackendService.deleteBackground(patient.patientId, this.apiVersion, this.token));

        // this is needed for the patient page, which has the current requested patient and not a list of patients
        patient.backgroundImage = null;

        // notify all other subscriber for patients
        await this.updatePatientsAndNotify();
    }

    public async deleteAvatar(patient: Patient): Promise<void> {
        await firstValueFrom(this.patientBackendService.deleteAvatar(patient.patientId, this.apiVersion, this.token));

        // this is needed for the patient page, which has the current requested patient and not a list of patients
        patient.avatarImage = null;

        // notify all other subscriber for patients
        await this.updatePatientsAndNotify();
    }

    public async updatePatientsAndNotify(filterPatientIds: string[] = null, opts?: PatientRetrieveOptions): Promise<Patient[]> {
        const patients = await this.getPatientsRequest(filterPatientIds, opts);
        this.applicationStateService.raiseEventSpecific(new PatientAddedOrRemovedEvent());
        this.patientsSource$$.next(patients);
        return patients;
    }

    public getWeights(patientId: string): Promise<WeightModel[]> {
        return firstValueFrom(this.patientBackendService.getWeights(patientId, this.apiVersion, this.token, false));
    }

    public deleteWeight(weightId: string): Promise<void> {
        return firstValueFrom(this.patientBackendService.markWeightAsHidden(weightId, this.apiVersion, this.token));
    }

    public createWeight(patientId: string, weight: WeightModel): Promise<WeightModel> {
        const weightCreateModel: WeightCreateModel = mapWeightModelIntoWeightCreateModel(weight);
        return firstValueFrom(this.patientBackendService.addWeight(weightCreateModel, this.apiVersion, this.token, patientId));
    }

    public async addPatientWithInsurance(patient: Patient | PatientDataUpdateModel): Promise<PatientCreateResultModel> {
        const patientData = patient instanceof Patient ? toPatientDataUpdateModel(patient) : patient;

        const result = await firstValueFrom(this.patientBackendService.create(patientData, this.apiVersion, this.token));

        await this.updatePatientsAndNotify();

        return result;
    }

    public async updatePatientWithInsurance(patient: Patient | PatientDataUpdateModel, id?: string): Promise<void> {
        if (!(patient instanceof Patient) && !id) return;
        const patientData = patient instanceof Patient ? toPatientDataUpdateModel(patient) : patient;
        const patientId = patient instanceof Patient ? patient.patientId : id;
        await firstValueFrom(this.patientBackendService.update(patientData, this.apiVersion, this.token, patientId));

        await this.updatePatientsAndNotify();
    }

    public getAllPatientInsurances(filterPatientIds?: string[]): Observable<PatientInsuranceModel[]> {
        return this.patientInsuranceAPI.getAll(this.apiVersion, this.token, filterPatientIds);
    }

    public addPatientInsurance(addModel: PatientinsuranceAddBody) {
        return this.patientInsuranceAPI.add(addModel, this.apiVersion, this.token);
    }

    public updatePatientInsurance(updateModel: PatientinsuranceUpdateBody) {
        return this.patientInsuranceAPI.update(updateModel, this.apiVersion, this.token);
    }

    public removePatientInsurance(insuranceId: string) {
        return this.patientInsuranceAPI.remove(insuranceId, this.apiVersion, this.token);
    }

    public getPrimaryAndEmergencyVeterinarians(patientId: string): Promise<PatientPrimaryAndEmergencyVeterinariansModel> {
        return firstValueFrom(this.patientBackendService.getPrimaryAndEmergencyVeterinarians(patientId, this.apiVersion, this.token));
    }

    public async updateMedicalData(medicalData: MedicalData): Promise<void> {
        await firstValueFrom(this.patientBackendService.saveMedicalData(medicalData, this.apiVersion, this.token));
        await this.updatePatientsAndNotify();
    }

    public savePrimaryAndEmergencyVeterinarians(model: PatientPrimaryAndEmergencyVeterinariansModel): Promise<void> {
        return firstValueFrom(this.patientBackendService.savePrimaryAndEmergencyVeterinarians(model, this.apiVersion, this.token));
    }

    public saveExtendedMedicalData(model: PatientExtendedMedicalDataModel): Promise<void> {
        return firstValueFrom(this.patientBackendService.saveExtendedMedicalData(model, this.apiVersion, this.token));
    }

    public onFavoriteReferenceDataForPatient(patientId: string): Observable<ReferenceDataFavoritesModel> {
        return this.patientBackendService.getFavoriteReferenceDataForPatient(patientId, this.apiVersion, this.token);
    }

    public validateRequiredPatientFields(patients: Patient[]): boolean {
        const lessOnPatientHasNotAllRequiredFieldsFilled = patients.some((patient: Patient) => {
            const { name, breedText, breedId, breedResourceKey, speciesText, speciesId, speciesResourceKey } = patient;

            const dateOfBirth = patient.animalSpecificData?.dateOfBirth;
            const genderId = patient.animalSpecificData?.genderId;
            const averageDateOfBirth = patient.livestockSpecificData?.averageDateOfBirth;

            return !(
                name &&
                (speciesText || (speciesId && speciesResourceKey)) &&
                (breedText || (breedId && breedResourceKey)) &&
                (averageDateOfBirth || dateOfBirth) &&
                (patient.animalSpecificData ? !!genderId : true)
            );
        });

        return !lessOnPatientHasNotAllRequiredFieldsFilled;
    }

    public getDocumentToFillDataForPatient(patientId: string): Observable<DocumentToFillPatientDataModel> {
        return this.patientBackendService.getDocumentToFillDataForPatient(patientId, this.apiVersion, this.token);
    }

    public getLinkagesForPatients(): Observable<Array<PatientLinkageModel>> {
        return this.patientBackendService.getLinkagesForPatients(this.apiVersion, this.token);
    }

    private isFoodProducingVisibleWithData(speciesId: string, speciesModels: SpeciesModel[]): boolean {
        const foundSpeciesModel = speciesModels.find((speciesModel) => speciesModel.id === speciesId);

        if (foundSpeciesModel) {
            return foundSpeciesModel.isFoodProducingVisible;
        }

        return true;
    }

    private async getPatientsRequest(filterPatientIds?: string[], opts?: PatientRetrieveOptions): Promise<Patient[]> {
        const { includeInsurances, filterDeceasedPatients, filterHiddenPatients } = getOptionDefaults(opts);
        const loadInsurances = includeInsurances && !this.featureService.isFeatureHidden(Feature.PatientInsurance);

        const obs = combineLatest([
            this.patientBackendService.getPatients(this.apiVersion, this.token, filterPatientIds),
            loadInsurances ? this.getAllPatientInsurances(filterPatientIds) : of([] as PatientInsuranceModel[]),
        ]).pipe(
            map(([patientModels, insuranceModels]) => {
                return patientModels
                    .map(
                        (patientModel) =>
                            new Patient(
                                patientModel,
                                insuranceModels.filter((i) => i.data.patientId === patientModel.patientId),
                            ),
                    )
                    .filter((p) => (!filterHiddenPatients || !p.isHidden) && (!filterDeceasedPatients || !p.deceased))
                    .sort(SortOrder.byName);
            }),
        );

        return firstValueFrom(obs);
    }
}
